import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import messageService from '../services/messageService';

const MessageContext = createContext({ unreadCount: 0, setUnreadCount: () => {}, newMessageTimestamp: null, markedReadTimestamp: null, decrementUnread: () => {}, notifyMarkedRead: () => {} });

export const MessageProvider = ({ children, userId }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [newMessageTimestamp, setNewMessageTimestamp] = useState(null);
  const [markedReadTimestamp, setMarkedReadTimestamp] = useState(null);
  const intervalRef = useRef(null);
  const connectionRef = useRef(null);
  const fetchStatsRef = useRef(null);
  const debounceRef = useRef(null);

  const fetchStats = useCallback(() => {
    if (!userId) return;
    messageService.getStats(userId)
      .then(res => setUnreadCount(res.data?.unreadCount || 0))
      .catch(() => {});
  }, [userId]);

  fetchStatsRef.current = fetchStats;

  useEffect(() => {
    fetchStats();
    intervalRef.current = setInterval(fetchStats, 60000);
    return () => clearInterval(intervalRef.current);
  }, [fetchStats]);


  useEffect(() => {
    if (!userId) return;

    const hubUrl = `${process.env.REACT_APP_MESSAGES_URL}/hubs/messages`;
    let cancelled = false;

    const connection = new HubConnectionBuilder()
      .withUrl(hubUrl)
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Information)
      .build();

    connectionRef.current = connection;

    connection.on('NewMessage', (message) => {
      console.log('[SignalR] NewMessage received:', message);
      setNewMessageTimestamp(Date.now());
      fetchStatsRef.current?.();
    });

    connection.on('StatsUpdated', () => {
      console.log('[SignalR] StatsUpdated received');
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => fetchStatsRef.current?.(), 400);
    });

    connection.on('MessageRead', () => {});

    connection.onreconnecting(() => console.warn('[SignalR] reconnecting...'));
    connection.onclose(err => console.warn('[SignalR] closed:', err));
    connection.onreconnected(() => {
      connection.invoke('JoinUserGroup', 6948).catch(() => {});
    });

    connection.start()
      .then(() => {
        if (cancelled) { connection.stop(); return; }
        return connection.invoke('JoinUserGroup', 6948);
      })
      .then(() => { if (!cancelled) console.log('[SignalR] connected ✓ joined group user_6948'); })
      .catch(err => console.error('[SignalR] failed:', err));

    return () => {
      cancelled = true;
      connection.stop();
    };
  }, [userId]);

  const decrementUnread = useCallback((count = 1) => {
    setUnreadCount(prev => Math.max(0, prev - count));
  }, []);

  const notifyMarkedRead = useCallback(() => setMarkedReadTimestamp(Date.now()), []);

  return (
    <MessageContext.Provider value={{ unreadCount, setUnreadCount, refreshStats: fetchStats, newMessageTimestamp, markedReadTimestamp, decrementUnread, notifyMarkedRead }}>
      {children}
    </MessageContext.Provider>
  );
};

export const useMessages = () => useContext(MessageContext);
